import { FormInstance, ModalProps } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { InvoiceDetails } from 'api/billing/types';
import { Form } from 'components/atoms/form';
import { Modal } from 'components/atoms/modal';
import { CreatableSelect } from 'components/atoms/select';
import { toastService } from 'components/molecules/toast';
import { useTranslate } from 'hooks/use-translation';
import { queryClient } from 'queries';
import {
  INVOICE_DETAILS_KEY,
  useUpdateInvoiceDetailsMutation,
} from 'queries/billing';
import { isValidEmail } from 'utils/is-valid-email';

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding-top: var(--layout-01) !important;
  }
`;

const EmailInput = (props) => {
  return (
    <CreatableSelect
      type="user"
      isClearable
      noOptionsMessage={() => null}
      {...props}
    />
  );
};

const EmailForm = ({
  form,
  invoiceDetails,
}: {
  form: FormInstance<any>;
  invoiceDetails: InvoiceDetails;
}) => {
  const translate = useTranslate();
  const [existingEmails, setExistingEmails] = useState<string[]>([]);
  const initialValues = useMemo(
    () => ({
      primaryEmail: {
        label: invoiceDetails.primaryEmail,
        value: invoiceDetails.primaryEmail,
      },
      secondaryEmails: invoiceDetails.secondaryEmails.map((_email) => ({
        label: _email,
        value: _email,
      })),
    }),
    [invoiceDetails.primaryEmail, invoiceDetails.secondaryEmails],
  );

  const isValidNewOption = useCallback(
    (email: string) => isValidEmail(email) && !existingEmails.includes(email),
    [existingEmails],
  );

  const onFormValuesChange = useCallback(() => {
    const formValues = form.getFieldsValue();
    const emails = [
      formValues.primaryEmail.value,
      ...formValues.secondaryEmails.map((_option) => _option.value),
    ];

    setExistingEmails(emails);
  }, [form]);

  return (
    <Form
      form={form}
      layout="vertical"
      colon={false}
      initialValues={initialValues}
      onChange={onFormValuesChange}
    >
      <Form.Item
        name="primaryEmail"
        required
        rules={[{ required: true, message: translate('input.required') }]}
        label={<FormattedMessage id="billing.emails.primary" />}
      >
        <EmailInput isValidNewOption={isValidNewOption} />
      </Form.Item>
      <Form.Item
        name="secondaryEmails"
        label={<FormattedMessage id="billing.emails.additional" />}
      >
        <EmailInput isMulti isValidNewOption={isValidNewOption} />
      </Form.Item>
    </Form>
  );
};

const getEmailUpdatePayload = ({
  primaryEmail,
  secondaryEmails,
}): Partial<InvoiceDetails> => {
  return {
    primaryEmail: primaryEmail?.emailId ?? primaryEmail?.value,
    secondaryEmails: secondaryEmails.map(
      (_option) => _option?.emailId ?? _option?.value,
    ),
  };
};

const hasChanges = (
  invoiceDetails: InvoiceDetails,
  payload: Partial<InvoiceDetails>,
): boolean => {
  return (
    invoiceDetails.primaryEmail !== payload.primaryEmail ||
    invoiceDetails.secondaryEmails.length !== payload.secondaryEmails?.length ||
    !!invoiceDetails.secondaryEmails.find(
      (_email) => !payload.secondaryEmails?.includes(_email),
    )
  );
};

export const BillingEmailsModal = ({
  close,
  invoiceDetails,
}: ModalProps & {
  invoiceDetails: InvoiceDetails;
  close: () => void;
}) => {
  const translate = useTranslate();
  const [form] = Form.useForm();
  const { mutateAsync: updateInvoiceDetails, isLoading } =
    useUpdateInvoiceDetailsMutation();

  const onSave = useCallback(async () => {
    try {
      const formValues = await form.validateFields();
      const payload = getEmailUpdatePayload(formValues);

      if (!hasChanges(invoiceDetails, payload)) {
        return;
      }

      await updateInvoiceDetails(payload);
      queryClient.invalidateQueries(INVOICE_DETAILS_KEY);
      close();
      toastService({
        title: translate('billing.edit.emails.toast.success.title'),
        message: translate('billing.edit.emails.toast.success.message'),
      }).success();
    } catch (e) {
      toastService({
        title: translate('billing.edit.emails.toast.error.title'),
        message: translate('billing.edit.emails.toast.error.message'),
      }).error();
    }
  }, [form, invoiceDetails, updateInvoiceDetails, close, translate]);

  return (
    <StyledModal
      visible
      okText={<FormattedMessage id="save" />}
      okButtonProps={{ loading: isLoading }}
      onOk={onSave}
      onCancel={close}
      title={<FormattedMessage id="billing.edit.emails.title" />}
    >
      <EmailForm form={form} invoiceDetails={invoiceDetails} />
    </StyledModal>
  );
};
